import React from 'react';
import { Box, Typography, Button, Zoom, Fade, useTheme } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import VideocamIcon from '@mui/icons-material/Videocam';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

/**
 * SuccessContent
 * --------------
 * The inner content of the success screen (check icon, title, CTAs, event cards).
 * Rendered inside whichever layout wrapper is active for the success page.
 *
 * onDark = true when placed over a dark image/video background.
 */
const SuccessContent = ({ result, onDark = false, variant }) => {
  const theme = useTheme();
  const { successPage, event, referenceId } = result ?? {};

  const page = successPage ?? {
    subtitle: 'Registration Complete',
    title: "You're registered!",
    message: "Thank you for signing up. We'll send you a confirmation email with all the details shortly.",
    showEventDetails: true,
    supportText: 'Questions? Contact us at support@example.com',
  };

  const activeEvent = event ?? {
    date: new Date().toISOString(),
    time: '10:00 AM',
    timezone: 'EST',
    platform: 'Zoom',
  };

  const activeReferenceId = referenceId ?? 'PREVIEW-0000';

  const eventDate = activeEvent.date
    ? new Date(activeEvent.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : null;

  const textColor = onDark ? '#ffffff' : 'text.primary';
  const subTextColor = onDark ? 'rgba(255,255,255,0.78)' : 'text.secondary';
  const captionColor = onDark ? 'rgba(255,255,255,0.6)' : 'primary.main';
  const cardBg = onDark ? 'rgba(255,255,255,0.12)' : 'background.paper';
  const cardBorder = onDark ? 'rgba(255,255,255,0.2)' : 'divider';
  const refBg = onDark ? 'rgba(255,255,255,0.12)' : '#f0f9ff';
  const refBorder = onDark ? 'rgba(255,255,255,0.2)' : '#bae6fd';
  const refTextColor = onDark ? '#000000' : '#0369a1';

  const successCard = (
    <Fade in timeout={600}>
      <Box sx={{ textAlign: 'center', py: { xs: 10, md: 10 }, }}>
        {/* Check icon with glow */}
        <Zoom in timeout={400}>
          <Box
            sx={{
              width: 100, height: 100, borderRadius: '50%',
              bgcolor: onDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.04)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              mx: 'auto', mb: 4,
              boxShadow: `0 0 0 16px ${theme.palette.primary.main}${onDark ? '30' : '10'}`,
            }}
          >
            <Box
              sx={{
                width: 72, height: 72, borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <CheckCircleIcon sx={{ color: '#fff', fontSize: 40 }} />
            </Box>
          </Box>
        </Zoom>

        {page.subtitle && (
          <Typography variant="caption" sx={{ letterSpacing: 2.5, color: captionColor, fontWeight: 700, fontSize: 11, textTransform: 'uppercase' }}>
            {page.subtitle}
          </Typography>
        )}

        {page.title && (
          <Typography variant="h4" fontWeight={800} sx={{ mt: 1, mb: 2, color: textColor }}>
            {page.title}
          </Typography>
        )}

        {page.message && (
          <Typography variant="body1" sx={{ color: subTextColor, maxWidth: 480, mx: 'auto', mb: 5 }}>
            {page.message}
          </Typography>
        )}

        {/* Event detail cards */}
        {page.showEventDetails !== false && (
          <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 6 }}>
            {eventDate && (
              <Box sx={{ px: 3, py: 2.5, borderRadius: 3, bgcolor: cardBg, border: '1px solid', borderColor: cardBorder, minWidth: 140, backdropFilter: 'blur(8px)' }}>
                <CalendarMonthIcon sx={{ color: subTextColor, fontSize: 22, mb: 0.5 }} />
                <Typography variant="caption" display="block" sx={{ color: subTextColor, letterSpacing: 1, fontSize: 10 }}>DATE</Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColor }}>{eventDate}</Typography>
              </Box>
            )}
            {activeEvent.time && (
              <Box sx={{ px: 3, py: 2.5, borderRadius: 3, bgcolor: cardBg, border: '1px solid', borderColor: cardBorder, minWidth: 140, backdropFilter: 'blur(8px)' }}>
                <AccessTimeIcon sx={{ color: subTextColor, fontSize: 22, mb: 0.5 }} />
                <Typography variant="caption" display="block" sx={{ color: subTextColor, letterSpacing: 1, fontSize: 10 }}>TIME</Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColor }}>{activeEvent.time} {activeEvent.timezone || ''}</Typography>
              </Box>
            )}
            {activeEvent.platform && (
              <Box sx={{ px: 3, py: 2.5, borderRadius: 3, bgcolor: cardBg, border: '1px solid', borderColor: cardBorder, minWidth: 140, backdropFilter: 'blur(8px)' }}>
                <VideocamIcon sx={{ color: subTextColor, fontSize: 22, mb: 0.5 }} />
                <Typography variant="caption" display="block" sx={{ color: subTextColor, letterSpacing: 1, fontSize: 10 }}>PLATFORM</Typography>
                <Typography variant="subtitle2" fontWeight={700} sx={{ color: textColor }}>{activeEvent.platform}</Typography>
              </Box>
            )}
          </Box>
        )}

        {/* CTAs */}
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap', mb: 4 }}>
          {page.ctaPrimary?.label && (
            <Button variant="contained" size="large" href={page.ctaPrimary.url || '#'}>
              {page.ctaPrimary.label}
            </Button>
          )}
          {page.ctaSecondary?.label && (
            <Button
              variant="outlined"
              size="large"
              href={page.ctaSecondary.url || '#'}
              sx={onDark ? { borderColor: 'rgba(255,255,255,0.5)', color: '#fff', '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } } : {}}
            >
              {page.ctaSecondary.label}
            </Button>
          )}
        </Box>

        {/* Reference ID */}
        {activeReferenceId && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 1, bgcolor: refBg, border: '1px solid', borderColor: refBorder, borderRadius: 2, px: 2.5, py: 1, mb: 3, backdropFilter: 'blur(8px)' }}>
            <Typography variant="caption" sx={{ color: refTextColor }}>
              Reference: <strong>{activeReferenceId}</strong>
            </Typography>
          </Box>
        )}

        {/* Support text */}
        {page.supportText && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            <InfoOutlinedIcon sx={{ fontSize: 16, color: onDark ? 'rgba(255,255,255,0.45)' : 'text.disabled' }} />
            <Typography variant="caption" sx={{ color: onDark ? 'rgba(255,255,255,0.55)' : 'text.disabled' }}>
              {page.supportText}
            </Typography>
          </Box>
        )}
      </Box>
    </Fade>
  )

  if (variant === 'card') {
    return (<Box
      sx={{
        width: '100%',
        backgroundColor: 'white',
        borderRadius: 4,
        border: '1px solid',
        borderColor: 'divider',
        boxShadow: '0 2px 24px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {successCard}
    </Box>)
  }
  if (variant === 'flush') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, bgcolor: 'background.paper' }}>
        {successCard}
      </Box>
    )
  }

  if (variant === 'incard') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, backgroundColor: 'white' }}>
        {successCard}

      </Box>
    )
  }

  return null;

};

export default SuccessContent;
